import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import SiteMessageSingleEditModal from './SiteMessageSingleEditModal';
import SiteMessageBroadcastEditModal from './SiteMessageBroadcastEditModal';
import SiteMessageViewModal from './SiteMessageViewModal';
import {
  BROADCAST_SEND_TYPE,
  buildBroadcastPageParams,
  buildInboxPageParams,
  formatBroadcastSendType,
  formatInboxStatus,
  INBOX_STATUS,
  INBOX_STATUS_OPTIONS,
  mergeInboxDetailsWithLanguages,
} from './siteMessageConstants';

const TABS = [
  { key: 'single', label: '單獨' },
  { key: 'broadcast', label: '群發' },
];

const SINGLE_EMPTY_FILTERS = { status: '', memberId: '', title: '' };
const BROADCAST_EMPTY_FILTERS = { title: '' };

export default function SiteMessagePage() {
  const [activeTab, setActiveTab] = useState('single');
  const [languages, setLanguages] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [singleFilters, setSingleFilters] = useState(SINGLE_EMPTY_FILTERS);
  const [singleRecords, setSingleRecords] = useState([]);
  const [singlePage, setSinglePage] = useState(1);
  const [singleTotalPages, setSingleTotalPages] = useState(1);
  const [singleTotal, setSingleTotal] = useState(0);
  const [singleLoading, setSingleLoading] = useState(true);

  const [broadcastFilters, setBroadcastFilters] = useState(BROADCAST_EMPTY_FILTERS);
  const [broadcastRecords, setBroadcastRecords] = useState([]);
  const [broadcastPage, setBroadcastPage] = useState(1);
  const [broadcastTotalPages, setBroadcastTotalPages] = useState(1);
  const [broadcastTotal, setBroadcastTotal] = useState(0);
  const [broadcastLoading, setBroadcastLoading] = useState(false);

  const [singleEditOpen, setSingleEditOpen] = useState(false);
  const [singleEditId, setSingleEditId] = useState(null);
  const [broadcastEditOpen, setBroadcastEditOpen] = useState(false);
  const [broadcastEditId, setBroadcastEditId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [deleteSingleTarget, setDeleteSingleTarget] = useState(null);
  const [deleteBroadcastTarget, setDeleteBroadcastTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    adminApi.newsLanguages().then(setLanguages).catch(() => {});
  }, []);

  async function loadSingle(pageNo = 1, filters = singleFilters) {
    setSingleLoading(true);
    setError('');
    try {
      const data = await adminApi.inboxPageQuery(buildInboxPageParams(pageNo, filters));
      setSingleRecords(data?.records || []);
      setSinglePage(data?.current || pageNo);
      setSingleTotalPages(data?.pages || 1);
      setSingleTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setSingleRecords([]);
    } finally {
      setSingleLoading(false);
    }
  }

  async function loadBroadcast(pageNo = 1, filters = broadcastFilters) {
    setBroadcastLoading(true);
    setError('');
    try {
      const data = await adminApi.broadcastInboxPageQuery(buildBroadcastPageParams(pageNo, filters));
      setBroadcastRecords(data?.records || []);
      setBroadcastPage(data?.current || pageNo);
      setBroadcastTotalPages(data?.pages || 1);
      setBroadcastTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setBroadcastRecords([]);
    } finally {
      setBroadcastLoading(false);
    }
  }

  useEffect(() => {
    loadSingle(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'broadcast') {
      loadBroadcast(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  async function openSingleView(row) {
    try {
      const data = await adminApi.getInbox(row.id);
      setViewData({
        ...data,
        statusLabel: formatInboxStatus(data.status),
        details: mergeInboxDetailsWithLanguages(languages, data.details),
      });
    } catch (err) {
      setError(err.message || '載入詳情失敗');
    }
  }

  async function openBroadcastView(row) {
    try {
      const data = await adminApi.getBroadcastInbox(row.id);
      setViewData({
        ...data,
        sendTypeLabel: formatBroadcastSendType(data.sendType),
        details: mergeInboxDetailsWithLanguages(languages, data.details),
      });
    } catch (err) {
      setError(err.message || '載入詳情失敗');
    }
  }

  async function confirmDeleteSingle() {
    if (!deleteSingleTarget?.id) {
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.deleteInbox(deleteSingleTarget.id);
      setMessage('刪除成功');
      setDeleteSingleTarget(null);
      await loadSingle(singlePage, singleFilters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setActionLoading(false);
    }
  }

  async function confirmDeleteBroadcast() {
    if (!deleteBroadcastTarget?.id) {
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.deleteBroadcastInbox(deleteBroadcastTarget.id);
      setMessage('刪除成功');
      setDeleteBroadcastTarget(null);
      await loadBroadcast(broadcastPage, broadcastFilters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>站內信</h2>
      </div>

      <div className="site-message-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`site-message-tabs__item ${activeTab === tab.key ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'single' ? (
        <>
          <form
            className="filter-bar filter-bar-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              setMessage('');
              loadSingle(1, singleFilters);
            }}
          >
            <div className="form-field">
              <label htmlFor="inboxFilterStatus">狀態</label>
              <select
                id="inboxFilterStatus"
                value={singleFilters.status}
                onChange={(e) => setSingleFilters((p) => ({ ...p, status: e.target.value }))}
              >
                {INBOX_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.label} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="inboxFilterMemberId">用戶ID</label>
              <input
                id="inboxFilterMemberId"
                type="text"
                value={singleFilters.memberId}
                onChange={(e) => setSingleFilters((p) => ({ ...p, memberId: e.target.value }))}
              />
            </div>
            <div className="form-field">
              <label htmlFor="inboxFilterTitle">標題</label>
              <input
                id="inboxFilterTitle"
                type="text"
                value={singleFilters.title}
                onChange={(e) => setSingleFilters((p) => ({ ...p, title: e.target.value }))}
                placeholder="請輸入站內信標題"
              />
            </div>
            <div className="filter-actions">
              <button type="submit" className="btn btn-primary">
                搜尋
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  const reset = { ...SINGLE_EMPTY_FILTERS };
                  setSingleFilters(reset);
                  setMessage('');
                  loadSingle(1, reset);
                }}
              >
                重置
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setSingleEditId(null);
                  setSingleEditOpen(true);
                  setMessage('');
                }}
              >
                新增
              </button>
            </div>
          </form>

          <div className="table-meta">共 {singleTotal} 筆</div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {['用戶ID', '標題（英語）', '狀態', '創建時間', '操作'].map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {singleLoading ? (
                  <tr>
                    <td colSpan={5} className="table-empty">
                      載入中…
                    </td>
                  </tr>
                ) : singleRecords.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="table-empty">
                      無資料
                    </td>
                  </tr>
                ) : (
                  singleRecords.map((row) => (
                    <tr key={row.id}>
                      <td>{row.memberId ?? '—'}</td>
                      <td className="cell-wrap">{row.enTitle || '未設定'}</td>
                      <td>
                        <span
                          className={
                            row.status === INBOX_STATUS.READ
                              ? 'status-text status-text--success'
                              : 'status-text'
                          }
                        >
                          {formatInboxStatus(row.status)}
                        </span>
                      </td>
                      <td>{formatDateTime(row.createTime)}</td>
                      <td>
                        <div className="table-actions table-actions--news">
                          <div className="table-actions__row">
                            <button type="button" className="btn btn-outline-neutral btn-sm" onClick={() => openSingleView(row)}>
                              詳情
                            </button>
                            <button type="button" className="btn btn-success btn-sm" onClick={() => { setSingleEditId(row.id); setSingleEditOpen(true); }}>
                              修改
                            </button>
                            <button type="button" className="btn btn-danger-outline btn-sm" onClick={() => setDeleteSingleTarget(row)}>
                              刪除
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={singlePage} totalPages={singleTotalPages} onChange={(next) => loadSingle(next, singleFilters)} />
        </>
      ) : (
        <>
          <form
            className="filter-bar filter-bar-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              setMessage('');
              loadBroadcast(1, broadcastFilters);
            }}
          >
            <div className="form-field">
              <label htmlFor="broadcastFilterTitle">標題</label>
              <input
                id="broadcastFilterTitle"
                type="text"
                value={broadcastFilters.title}
                onChange={(e) => setBroadcastFilters((p) => ({ ...p, title: e.target.value }))}
                placeholder="請輸入內容/標題"
              />
            </div>
            <div className="filter-actions">
              <button type="submit" className="btn btn-primary">
                查詢
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  const reset = { ...BROADCAST_EMPTY_FILTERS };
                  setBroadcastFilters(reset);
                  setMessage('');
                  loadBroadcast(1, reset);
                }}
              >
                重置
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setBroadcastEditId(null);
                  setBroadcastEditOpen(true);
                  setMessage('');
                }}
              >
                新增
              </button>
            </div>
          </form>

          <div className="table-meta">共 {broadcastTotal} 筆</div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {['用戶ID', '標題（英語）', '創建時間', '操作'].map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {broadcastLoading ? (
                  <tr>
                    <td colSpan={4} className="table-empty">
                      載入中…
                    </td>
                  </tr>
                ) : broadcastRecords.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="table-empty">
                      無資料
                    </td>
                  </tr>
                ) : (
                  broadcastRecords.map((row) => (
                    <tr key={row.id}>
                      <td>{row.sendType === BROADCAST_SEND_TYPE.ALL ? '所有用戶' : formatBroadcastSendType(row.sendType)}</td>
                      <td className="cell-wrap">{row.enTitle || '未設定'}</td>
                      <td>{formatDateTime(row.createTime)}</td>
                      <td>
                        <div className="table-actions table-actions--news">
                          <div className="table-actions__row">
                            <button type="button" className="btn btn-outline-neutral btn-sm" onClick={() => openBroadcastView(row)}>
                              詳情
                            </button>
                            <button type="button" className="btn btn-success btn-sm" onClick={() => { setBroadcastEditId(row.id); setBroadcastEditOpen(true); }}>
                              修改
                            </button>
                            <button type="button" className="btn btn-danger-outline btn-sm" onClick={() => setDeleteBroadcastTarget(row)}>
                              刪除
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={broadcastPage} totalPages={broadcastTotalPages} onChange={(next) => loadBroadcast(next, broadcastFilters)} />
        </>
      )}

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <SiteMessageSingleEditModal
        open={singleEditOpen}
        inboxId={singleEditId}
        onClose={() => setSingleEditOpen(false)}
        onSaved={(wasEdit) => {
          setMessage(wasEdit ? '修改成功' : '新增成功');
          loadSingle(singlePage, singleFilters);
        }}
      />

      <SiteMessageBroadcastEditModal
        open={broadcastEditOpen}
        broadcastId={broadcastEditId}
        onClose={() => setBroadcastEditOpen(false)}
        onSaved={(wasEdit) => {
          setMessage(wasEdit ? '修改成功' : '群發成功');
          loadBroadcast(broadcastPage, broadcastFilters);
        }}
      />

      <SiteMessageViewModal
        open={Boolean(viewData)}
        title={viewData?.memberId != null ? `詳情 [${viewData.id}]` : `詳情 [${viewData?.id || ''}]`}
        data={viewData}
        onClose={() => setViewData(null)}
      />

      <ConfirmDialog
        open={Boolean(deleteSingleTarget)}
        title="刪除站內信"
        message="確定刪除此站內信？"
        confirmLabel={actionLoading ? '刪除中…' : '刪除'}
        confirmDisabled={actionLoading}
        onConfirm={confirmDeleteSingle}
        onCancel={() => setDeleteSingleTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(deleteBroadcastTarget)}
        title="刪除群發站內信"
        message="確定刪除此群發站內信？將一併刪除已發送的收件記錄。"
        confirmLabel={actionLoading ? '刪除中…' : '刪除'}
        confirmDisabled={actionLoading}
        onConfirm={confirmDeleteBroadcast}
        onCancel={() => setDeleteBroadcastTarget(null)}
      />
    </div>
  );
}
